import { TempoObject } from '../models/TempoObject.js';
import { KeyMarking } from '../models/KeyMarking.js';
import { TimeSignatureObject } from '../models/TimeSignatureObject.js';

/**
 * Service for handling song metadata operations (tempo, time signatures, key changes, etc.)
 */
export class MetadataService {
  constructor(annotationDataService) {
    this.dataService = annotationDataService;
  }

  /**
   * Get sheet's general information in UI format
   */
  getSongGeneralInfoInUiFormat() {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) {
      console.warn('Annotation sheet not initialized');
      return {};
    }
    
    if (annotationSheet.general_info) {
      const formData = {
        sheet_id: annotationSheet.general_info.sheet_id,
        assigned_annotators: annotationSheet.general_info.assigned_annotators,
        original_filename: annotationSheet.general_info.original_filename,
        displayed_filename: annotationSheet.general_info.displayed_filename,
        file_path: annotationSheet.general_info.file_path,
        song_title: annotationSheet.general_info.song_title,
        artist: annotationSheet.general_info.artist,
        lyricist: annotationSheet.general_info.lyricist,
        songwriter: annotationSheet.general_info.songwriter,
        arranger: annotationSheet.general_info.arranger,
        composer: annotationSheet.general_info.composer,
        year_of_publication: annotationSheet.general_info.year_of_publication,
        presence_of_main_melody: annotationSheet.general_info.presence_of_main_melody,
        music_type: annotationSheet.general_info.music_type,
        duration_number_of_bars: annotationSheet.general_info.duration.number_of_bars,
        duration_time_length: annotationSheet.general_info.duration.time_length,
        music_completeness: annotationSheet.general_info.music_completeness,
        music_compositional_craftsmanship: annotationSheet.general_info.music_compositional_craftsmanship,
        emotion: annotationSheet.general_info.emotion,
        imagery: annotationSheet.general_info.imagery,
        regional_classification: annotationSheet.general_info.regional_classification,
        music_style: annotationSheet.general_info.music_style,
        music_style_description: annotationSheet.general_info.music_style_description,
        professional_description: annotationSheet.general_info.professional_description,
        casual_description: annotationSheet.general_info.casual_description,
        completed: Boolean(annotationSheet.general_info.completed),
      }
      return formData
    }
    return {};
  }

  /**
   * Get tempo changes in UI format
   */
  getTempoChangesInUiFormat() {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) {
      console.warn('Annotation sheet not initialized');
      return {};
    }

    const uiTempoChanges = annotationSheet.tempos.map(tempo => {
      return {
        position: {
          bar: tempo.start_time.bar,
          beat: tempo.start_time.beat,
          subdivision: tempo.start_time.subdivision
        },
        value: tempo.tempo
      }
    });

    return uiTempoChanges;
  }

  /**
   * Get key changes in UI format
   */
  getKeyChangesInUiFormat() {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) {
      console.warn('Annotation sheet not initialized');
      return [];
    }

    const uiKeyChanges = annotationSheet.keys.map(key => {
      return {
        position: {
          bar: key.start_time.bar,
          beat: key.start_time.beat,
          subdivision: key.start_time.subdivision
        },
        value: {
          note: key.key.note,
          mode: key.key.mode,
          accidental: key.key.sharp_flat
        }
      }
    });
    
    return uiKeyChanges;
  }

  /**
   * Get time signatures in UI format
   */
  getTimeSignaturesInUiFormat() {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) {
      console.warn('Annotation sheet not initialized');
      return [];
    }
    
    const uiTimeSignatures = annotationSheet.time_signatures.map(timeSignature => {
      return {
        position: {
          bar: timeSignature.start_time.bar,
          beat: timeSignature.start_time.beat,
          subdivision: timeSignature.start_time.subdivision
        },
        value: `${timeSignature.time_signature.num_of_beats}/${timeSignature.time_signature.note_value}`
      };
    });
    
    return uiTimeSignatures;
  }

  /**
   * Get all annotations in UI format
   */
  getAllAnnotationsInUiFormat() {
    return this.dataService.annotationBlockStore.getAllBlocks();
  }

  /**
   * Add a new tempo change
   */
  addTempoChange(tempoChangeData) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) {
      console.warn('Annotation sheet not initialized');
      return { success: false, error: 'Annotation sheet not initialized' };
    }
  
    try {
      if (!this.dataService.isValidPosition(tempoChangeData, tempoChangeData.position)) {
        return { success: false, error: "Cannot add tempo change at a position earlier than existing changes" };
      }

      // Create tempo change structure
      const newTempoChange = {
        start_time: {
          bar: tempoChangeData.position.bar,
          beat: tempoChangeData.position.beat,
          subdivision: tempoChangeData.position.subdivision
        },
        tempo: tempoChangeData.value 
      };

      // Create TempoObject
      const tempoChangeObj = new TempoObject(newTempoChange);

      // Add to annotation sheet
      annotationSheet.tempos.push(tempoChangeObj);
      
      // Sort tempos by position
      this.sortTempos();
      
      // Notify listeners
      this.dataService.notifyListeners();

      return {success: true};
    } catch (error) {
      console.error("Error adding tempo change:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update an existing tempo change
   */
  updateTempoChange(index, tempoChangeData) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) {
      console.warn('Annotation sheet not initialized');
      return { success: false, error: 'Annotation sheet not initialized' };
    }

    try {
      if (!this.dataService.isValidPosition(tempoChangeData, tempoChangeData.position)) {
        return { success: false, error: "Cannot add tempo change at a position earlier than existing changes" };
      }

      if (index < 0 || index >= annotationSheet.tempos.length) {
        return { success: false, error: 'Invalid tempo change index' };
      }

      // Create tempo change structure
      const updatedTempoChange = {
        start_time: {
          bar: tempoChangeData.position.bar,
          beat: tempoChangeData.position.beat,
          subdivision: tempoChangeData.position.subdivision
        },
        tempo: tempoChangeData.value 
      };

      // Update in annotation sheet
      annotationSheet.tempos[index] = new TempoObject(updatedTempoChange);
      
      // Sort tempos by position
      this.sortTempos();
      
      // Notify listeners
      this.dataService.notifyListeners();
      
      return { success: true };
    } catch (error) {
      console.error("Error updating tempo change:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete a tempo change
   */
  deleteTempoChange(index) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) {
      console.warn('Annotation sheet not initialized');
      return { success: false, error: 'Annotation sheet not initialized' };
    }

    try {
      if (index < 0 || index >= annotationSheet.tempos.length) {
        return { success: false, error: 'Invalid tempo change index' };
      }

      // Remove from annotation sheet
      annotationSheet.tempos.splice(index, 1);
      
      // Notify listeners
      this.dataService.notifyListeners();
      
      return { success: true };
    } catch (error) {
      console.error("Error deleting tempo change:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Apply batch tempo changes: clear existing and apply new list
   */
  applyBatchTempoChanges(tempoChanges) {
    const sheet = this.dataService.getAnnotationSheet();
    if (!sheet) {
      console.warn('Annotation sheet not initialized');
      return { success: false, error: 'Annotation sheet not initialized' };
    }
    try {
      sheet.tempos = [];
      tempoChanges.forEach(tc => {
        const obj = { start_time: { ...tc.position }, tempo: tc.value };
        sheet.tempos.push(new TempoObject(obj));
      });
      this.sortTempos();
      this.dataService.notifyListeners();
      return { success: true };
    } catch (err) {
      console.error('Error applying batch tempo changes:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Add a new key change
   */
  addKeyChange(keyChangeData) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) {
      console.warn('Annotation sheet not initialized');
      return { success: false, error: 'Annotation sheet not initialized' };
    }

    try {
      // Create key change structure
      if (!this.dataService.isValidPosition(keyChangeData, keyChangeData.position)) {
        return { success: false, error: "Cannot add key change at a position earlier than existing changes" };
      }

      const newKeyChange = {
        start_time: {
          bar: keyChangeData.position.bar,
          beat: keyChangeData.position.beat,
          subdivision: keyChangeData.position.subdivision
        },
        key: {
          sharp_flat: keyChangeData.value.accidental,
          note: keyChangeData.value.note,
          mode: keyChangeData.value.mode
        }
      };

      // Create KeyMarking object
      const keyChangeObj = new KeyMarking(newKeyChange);

      // Add to annotation sheet
      annotationSheet.keys.push(keyChangeObj);
      
      // Sort keys by position
      this.sortKeys();
      
      // Notify listeners
      this.dataService.notifyListeners();

      return { success: true };
    } catch (error) {
      console.error("Error adding key change:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update an existing key change
   */
  updateKeyChange(index, keyChangeData) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) {
      console.warn('Annotation sheet not initialized');
      return { success: false, error: 'Annotation sheet not initialized' };
    }

    try {
      if (!this.dataService.isValidPosition(keyChangeData, keyChangeData.position)) {
        return { success: false, error: "Cannot add key change at a position earlier than existing changes" };
      }
  
      if (index < 0 || index >= annotationSheet.keys.length) {
        return { success: false, error: 'Invalid key change index' };
      }

      // Create key change structure
      const updatedKeyChange = {
        start_time: {
          bar: keyChangeData.position.bar,
          beat: keyChangeData.position.beat,
          subdivision: keyChangeData.position.subdivision
        },
        key: {
          sharp_flat: keyChangeData.value.accidental,
          note: keyChangeData.value.note,
          mode: keyChangeData.value.mode
        }
      };

      // Update in annotation sheet
      annotationSheet.keys[index] = new KeyMarking(updatedKeyChange);
      
      // Sort keys by position
      this.sortKeys();
      
      // Notify listeners
      this.dataService.notifyListeners();
      
      return { success: true };
    } catch (error) {
      console.error("Error updating key change:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete a key change
   */
  deleteKeyChange(index) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) {
      console.warn('Annotation sheet not initialized');
      return { success: false, error: 'Annotation sheet not initialized' };
    }

    try {
      if (index < 0 || index >= annotationSheet.keys.length) {
        return { success: false, error: 'Invalid key change index' };
      }

      // Remove from annotation sheet
      annotationSheet.keys.splice(index, 1);
      
      // Notify listeners
      this.dataService.notifyListeners();
      
      return { success: true };
    } catch (error) {
      console.error("Error deleting key change:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Apply batch key changes: clear existing and apply new list
   */
  applyBatchKeyChanges(keyChanges) {
    const sheet = this.dataService.getAnnotationSheet();
    if (!sheet) {
      console.warn('Annotation sheet not initialized');
      return { success: false, error: 'Annotation sheet not initialized' };
    }
    try {
      sheet.keys = [];
      keyChanges.forEach(kc => {
        const obj = {
          start_time: { ...kc.position },
          key: { sharp_flat: kc.value.accidental, note: kc.value.note, mode: kc.value.mode }
        };
        sheet.keys.push(new KeyMarking(obj));
      });
      this.sortKeys();
      this.dataService.notifyListeners();
      return { success: true };
    } catch (err) {
      console.error('Error applying batch key changes:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Check if a time signature change is necessary
   * A change is necessary if it's different from the time signature that would apply at that position
   */
  isTimeSignatureChangeNecessary(newTimeSignatureData) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) {
      return true; // If no annotation sheet, assume change is necessary
    }
    
    // If this is the first time signature (bar 1, beat 1, subdivision 1), it's always necessary
    if (newTimeSignatureData.position.bar === 1 && 
        newTimeSignatureData.position.beat === 1 && 
        newTimeSignatureData.position.subdivision === 1) {
      return true;
    }
    
    // Sort existing time signatures by position
    const timeSignatures = [...annotationSheet.time_signatures];
    timeSignatures.sort((a, b) => {
      if (a.start_time.bar !== b.start_time.bar) {
        return a.start_time.bar - b.start_time.bar;
      }
      if (a.start_time.beat !== b.start_time.beat) {
        return a.start_time.beat - b.start_time.beat;
      }
      return a.start_time.subdivision - b.start_time.subdivision;
    });
    
    // Find the latest time signature that applies before the new position
    let applicableTimeSignature = null;
    for (const ts of timeSignatures) {
      if (ts.start_time.bar < newTimeSignatureData.position.bar || 
          (ts.start_time.bar === newTimeSignatureData.position.bar && 
           ts.start_time.beat < newTimeSignatureData.position.beat) ||
          (ts.start_time.bar === newTimeSignatureData.position.bar && 
           ts.start_time.beat === newTimeSignatureData.position.beat &&
           ts.start_time.subdivision <= newTimeSignatureData.position.subdivision)) {
        applicableTimeSignature = ts;
      } else {
        break; // We've gone past the position
      }
    }
    
    // If no previous time signature found, this one is necessary
    if (!applicableTimeSignature) {
      return true;
    }
    
    // Compare the time signature values
    const existingValue = `${applicableTimeSignature.time_signature.num_of_beats}/${applicableTimeSignature.time_signature.note_value}`;
    return existingValue !== newTimeSignatureData.value;
  }

  /**
   * Add a new time signature
   */
  addTimeSignature(timeSignatureData) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) {
      console.warn('Annotation sheet not initialized');
      return { success: false, error: 'Annotation sheet not initialized' };
    }

    try {
      // Check if this time signature change is necessary
      if (!this.isTimeSignatureChangeNecessary(timeSignatureData)) {
        return { 
          success: false, 
          error: 'Unnecessary time signature change. This measure already has this time signature.' 
        };
      }

      // Create time signature structure
      const newTimeSignature = {
        start_time: {
          bar: timeSignatureData.position.bar,
          beat: timeSignatureData.position.beat,
          subdivision: timeSignatureData.position.subdivision
        },
        time_signature: this.parseTimeSignatureValue(timeSignatureData.value)
      };

      // Create TimeSignatureObject
      const timeSignatureObj = new TimeSignatureObject(newTimeSignature);

      // Add to annotation sheet
      annotationSheet.time_signatures.push(timeSignatureObj);
      
      // Check if we need to add a "restoring" time signature for the next measure
      this.addRestoringTimeSignatureIfNeeded(timeSignatureData);
      
      // Sort time signatures by position
      this.sortTimeSignatures();
      
      // Notify listeners
      this.dataService.notifyListeners();
      
      return { success: true };
    } catch (error) {
      console.error("Error adding time signature:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Add a restoring time signature for the next measure if needed
   * This prevents a time signature change at one measure from affecting all subsequent measures
   */
  addRestoringTimeSignatureIfNeeded(timeSignatureData) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) return;
    
    const currentMeasure = timeSignatureData.position.bar;
    const nextMeasure = currentMeasure + 1;
    
    // Skip if we're already at the last possible measure
    if (nextMeasure > 999) return; // Arbitrary high limit
    
    // Check if there's already a time signature defined for the next measure
    const hasNextMeasureTimeSignature = annotationSheet.time_signatures.some(ts => 
      ts.start_time.bar === nextMeasure && ts.start_time.beat === 1 && ts.start_time.subdivision === 1
    );
    
    // If next measure already has a time signature, no need to add a restoring one
    if (hasNextMeasureTimeSignature) return;
    
    // Find the time signature that should apply after this change
    // This is the time signature that would apply to measure (currentMeasure+1) 
    // if the current change didn't exist
    
    // First, temporarily remove the time signature we just added
    const tempTimeSignatures = [...annotationSheet.time_signatures];
    const justAddedIndex = tempTimeSignatures.findIndex(ts => 
      ts.start_time.bar === currentMeasure && 
      ts.start_time.beat === timeSignatureData.position.beat && 
      ts.start_time.subdivision === timeSignatureData.position.subdivision
    );
    
    if (justAddedIndex !== -1) {
      tempTimeSignatures.splice(justAddedIndex, 1);
    }
    
    // Sort by position to find the applicable time signature
    tempTimeSignatures.sort((a, b) => {
      if (a.start_time.bar !== b.start_time.bar) {
        return a.start_time.bar - b.start_time.bar;
      }
      if (a.start_time.beat !== b.start_time.beat) {
        return a.start_time.beat - b.start_time.beat;
      }
      return a.start_time.subdivision - b.start_time.subdivision;
    });
    
    // Find the latest time signature that would apply to the next measure
    let applicableTimeSignature = null;
    for (const ts of tempTimeSignatures) {
      if (ts.start_time.bar <= nextMeasure) {
        applicableTimeSignature = ts;
      } else {
        break; // We've gone past the measure
      }
    }
    
    // If we found an applicable time signature, and it's different from the one we just added,
    // add a restoring time signature for the next measure
    if (applicableTimeSignature) {
      const applicableValue = `${applicableTimeSignature.time_signature.num_of_beats}/${applicableTimeSignature.time_signature.note_value}`;
      
      // Only add if the applicable time signature is different from the one we just added
      if (applicableValue !== timeSignatureData.value) {
        const restoringTimeSignature = {
          start_time: {
            bar: nextMeasure,
            beat: 1,
            subdivision: 1
          },
          time_signature: {
            num_of_beats: applicableTimeSignature.time_signature.num_of_beats,
            note_value: applicableTimeSignature.time_signature.note_value
          }
        };
        
        // Add the restoring time signature
        annotationSheet.time_signatures.push(new TimeSignatureObject(restoringTimeSignature));
      }
    }
  }

  /**
   * Update an existing time signature
   */
  updateTimeSignature(index, timeSignatureData) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) {
      console.warn('Annotation sheet not initialized');
      return { success: false, error: 'Annotation sheet not initialized' };
    }

    try {
      if (index < 0 || index >= annotationSheet.time_signatures.length) {
        return { success: false, error: 'Invalid time signature index' };
      }

      // Get the current time signature for comparison
      const currentTimeSignature = annotationSheet.time_signatures[index];
      const currentValue = `${currentTimeSignature.time_signature.num_of_beats}/${currentTimeSignature.time_signature.note_value}`;
      const currentBar = currentTimeSignature.start_time.bar;
      
      // If the position or value hasn't changed, no need to check necessity
      const samePosition = currentBar === timeSignatureData.position.bar;
      const sameValue = currentValue === timeSignatureData.value;
      
      // If both position and value are the same, nothing to update
      if (samePosition && sameValue) {
        return { success: true }; // No change needed
      }
      
      // If position changed or value changed, check if the update is necessary
      if ((samePosition && !sameValue) || (!samePosition && sameValue)) {
        // Temporarily remove the current time signature to check if the new one is necessary
        const tempTimeSignatures = [...annotationSheet.time_signatures];
        tempTimeSignatures.splice(index, 1);
        annotationSheet.time_signatures = tempTimeSignatures;
        
        const necessary = this.isTimeSignatureChangeNecessary(timeSignatureData);
        
        // Restore the original time signatures
        annotationSheet.time_signatures = [...tempTimeSignatures];
        annotationSheet.time_signatures.splice(index, 0, currentTimeSignature);
        
        if (!necessary) {
          return { 
            success: false, 
            error: 'Unnecessary time signature change. This time signature already applies at this position.' 
          };
        }
      }

      // Store the original time signature for reference
      const originalTimeSignature = annotationSheet.time_signatures[index];
      const originalMeasure = originalTimeSignature.start_time.bar;
      const originalValue = `${originalTimeSignature.time_signature.num_of_beats}/${originalTimeSignature.time_signature.note_value}`;

      // Create time signature structure
      const updatedTimeSignature = {
        start_time: {
          bar: timeSignatureData.position.bar,
          beat: timeSignatureData.position.beat,
          subdivision: timeSignatureData.position.subdivision
        },
        time_signature: this.parseTimeSignatureValue(timeSignatureData.value)
      };

      // Update in annotation sheet
      annotationSheet.time_signatures[index] = new TimeSignatureObject(updatedTimeSignature);
      
      // If we're changing the time signature value and not the position, 
      // check if we need to add a restoring time signature for the next measure
      if (samePosition && !sameValue) {
        this.addRestoringTimeSignatureIfNeeded(timeSignatureData);
      }
      
      // If we're changing the position of the time signature, we need to handle more complex cases
      if (!samePosition) {
        // Clean up and adjust any time signatures affected by the move
        this.handleTimeSignatureRepositioning(originalMeasure, originalValue, timeSignatureData);
      }
      
      // Sort time signatures by position
      this.sortTimeSignatures();
      
      // Notify listeners
      this.dataService.notifyListeners();
      
      return { success: true };
    } catch (error) {
      console.error("Error updating time signature:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Handle the complex logic of repositioning a time signature
   * This ensures that measures affected by the move maintain their intended time signatures
   */
  handleTimeSignatureRepositioning(originalMeasure, originalValue, newTimeSignatureData) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) return;

    // const newMeasure = newTimeSignatureData.position.bar;
    
    // For both the original location and the new location, check if we need 
    // to add restoring time signatures to maintain the correct behavior
    
    // Case 1: Check if we need to add a time signature at the original location 
    // to maintain what was previously there
    const originalNextMeasure = originalMeasure + 1;
    
    // Check if there's already a time signature at the next measure after the original
    const hasOriginalNextTimeSignature = annotationSheet.time_signatures.some(ts => 
      ts.start_time.bar === originalNextMeasure && ts.start_time.beat === 1 && ts.start_time.subdivision === 1
    );
    
    // If there isn't a time signature at the next measure, we need to find what time signature 
    // applied before our change and add it back
    if (!hasOriginalNextTimeSignature) {
      // Find the time signature that should apply before the original time signature
      // Pretend the original time signature isn't there
      const tempTimeSignatures = [...annotationSheet.time_signatures].filter(ts => 
        ts.start_time.bar !== originalMeasure || 
        ts.start_time.beat !== 1 || 
        ts.start_time.subdivision !== 1
      );
      
      // Find the last time signature before the original
      tempTimeSignatures.sort((a, b) => {
        if (a.start_time.bar !== b.start_time.bar) {
          return a.start_time.bar - b.start_time.bar;
        }
        if (a.start_time.beat !== b.start_time.beat) {
          return a.start_time.beat - b.start_time.beat;
        }
        return a.start_time.subdivision - b.start_time.subdivision;
      });
      
      let applicableTimeSignature = null;
      for (const ts of tempTimeSignatures) {
        if (ts.start_time.bar < originalMeasure) {
          applicableTimeSignature = ts;
        } else {
          break;
        }
      }
      
      // If we found a previous time signature, add it at the original location
      if (applicableTimeSignature) {
        const previousValue = `${applicableTimeSignature.time_signature.num_of_beats}/${applicableTimeSignature.time_signature.note_value}`;
        
        // Only add if it's different from the original
        if (previousValue !== originalValue) {
          const originalPositionTimeSignature = {
            start_time: {
              bar: originalMeasure,
              beat: 1,
              subdivision: 1
            },
            time_signature: {
              num_of_beats: applicableTimeSignature.time_signature.num_of_beats,
              note_value: applicableTimeSignature.time_signature.note_value
            }
          };
          
          // Add the time signature
          annotationSheet.time_signatures.push(new TimeSignatureObject(originalPositionTimeSignature));
        }
      }
    }
    
    // Case 2: Add a restoring time signature after the new position
    this.addRestoringTimeSignatureIfNeeded(newTimeSignatureData);
  }

  /**
   * Delete a time signature
   */
  deleteTimeSignature(index) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) {
      console.warn('Annotation sheet not initialized');
      return { success: false, error: 'Annotation sheet not initialized' };
    }

    try {
      if (index < 0 || index >= annotationSheet.time_signatures.length) {
        return { success: false, error: 'Invalid time signature index' };
      }

      // Remove from annotation sheet
      annotationSheet.time_signatures.splice(index, 1);
      
      // Notify listeners
      this.dataService.notifyListeners();
      
      return { success: true };
    } catch (error) {
      console.error("Error deleting time signature:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Apply batch time signature changes
   */
  applyBatchTimeSignatureChanges(timeSignatureRanges) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet) {
      console.warn('Annotation sheet not initialized');
      return { success: false, error: 'Annotation sheet not initialized' };
    }

    try {
      // Get existing time signatures before we clear them (for restoration purposes)
      const existingTimeSignatures = [...annotationSheet.time_signatures];
      
      // Clear existing time signatures
      annotationSheet.time_signatures = [];
      
      // Sort the ranges by start measure
      timeSignatureRanges.sort((a, b) => a.start - b.start);
      
      // Filter out unnecessary time signatures (those that don't represent a change)
      const optimizedRanges = [];
      let currentTimeSignature = null;
      
      for (const range of timeSignatureRanges) {
        // First time signature is always necessary
        if (currentTimeSignature === null) {
          optimizedRanges.push(range);
          currentTimeSignature = range.timeSignature;
        } 
        // Only add a new time signature if it's different from the current one
        else if (range.timeSignature !== currentTimeSignature) {
          optimizedRanges.push(range);
          currentTimeSignature = range.timeSignature;
        }
      }
      
      // Find the highest measure number in the original sheet
      let maxMeasure = 1;
      if (existingTimeSignatures.length > 0) {
        maxMeasure = Math.max(...existingTimeSignatures.map(ts => ts.start_time.bar));
      }
      
      // Now we need to determine what time signature applies at the end of our batch changes
      if (optimizedRanges.length > 0) {
        const lastRange = optimizedRanges[optimizedRanges.length - 1];
        const lastRangeEnd = lastRange.end;
        
        // Find any pre-existing time signatures that come after our batch changes
        const laterTimeSignatures = existingTimeSignatures.filter(ts => ts.start_time.bar > lastRangeEnd);
        
        // If there are any, ensure we properly connect our batch ranges to them
        if (laterTimeSignatures.length > 0) {
          laterTimeSignatures.sort((a, b) => a.start_time.bar - b.start_time.bar);
          const firstLaterTs = laterTimeSignatures[0];
          const firstLaterTsBar = firstLaterTs.start_time.bar;
          
          // Only add a connecting time signature if our batch changes end before the next existing one
          if (lastRangeEnd < firstLaterTsBar - 1) {
            // Find what time signature would have applied at the end of our batch changes in the original sheet
            let applicableTimeSignature = null;
            const endMeasure = lastRangeEnd + 1;
            
            existingTimeSignatures.sort((a, b) => a.start_time.bar - b.start_time.bar);
            for (const ts of existingTimeSignatures) {
              if (ts.start_time.bar <= endMeasure) {
                applicableTimeSignature = ts;
              } else {
                break;
              }
            }
            
            // If we found an applicable time signature and it's different from our last batch time signature,
            // add it to restore the correct time signature after our batch changes
            if (applicableTimeSignature) {
              const applicableValue = `${applicableTimeSignature.time_signature.num_of_beats}/${applicableTimeSignature.time_signature.note_value}`;
              
              if (applicableValue !== lastRange.timeSignature) {
                // Add this to our optimized ranges
                optimizedRanges.push({
                  start: endMeasure,
                  end: firstLaterTsBar - 1,
                  timeSignature: applicableValue
                });
              }
            }
          }
          
          // Now add all the later time signatures from the original sheet
          for (const ts of laterTimeSignatures) {
            const value = `${ts.time_signature.num_of_beats}/${ts.time_signature.note_value}`;
            
            // Create time signature structure
            const newTimeSignature = {
              start_time: {
                bar: ts.start_time.bar,
                beat: ts.start_time.beat,
                subdivision: ts.start_time.subdivision
              },
              time_signature: this.parseTimeSignatureValue(value)
            };
            
            // Add to annotation sheet
            annotationSheet.time_signatures.push(new TimeSignatureObject(newTimeSignature));
          }
        }
      }
      
      // Add each optimized time signature from ranges
      for (const range of optimizedRanges) {
        const timeSignatureData = {
          position: {
            bar: range.start,
            beat: 1,
            subdivision: 1
          },
          value: range.timeSignature
        };
        
        // Create time signature structure
        const newTimeSignature = {
          start_time: {
            bar: timeSignatureData.position.bar,
            beat: timeSignatureData.position.beat,
            subdivision: timeSignatureData.position.subdivision
          },
          time_signature: this.parseTimeSignatureValue(timeSignatureData.value)
        };

        // Create TimeSignatureObject
        const timeSignatureObj = new TimeSignatureObject(newTimeSignature);
        
        // Add to annotation sheet
        annotationSheet.time_signatures.push(timeSignatureObj);
      }
      
      // Sort time signatures by position
      this.sortTimeSignatures();
      
      // Notify listeners
      this.dataService.notifyListeners();
      
      return { success: true };
    } catch (error) {
      console.error("Error applying batch time signature changes:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Parse time signature string (e.g., "4/4") into object
   */
  parseTimeSignatureValue(timeSignatureStr) {
    const [numerator, denominator] = timeSignatureStr.split('/').map(num => parseInt(num, 10));
    
    if (isNaN(numerator) || isNaN(denominator)) {
      throw new Error('Invalid time signature format');
    }
    
    return {
      num_of_beats: numerator,
      note_value: denominator
    };
  }

  /**
   * Sort time signatures by position
   */
  sortTimeSignatures() {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet || !annotationSheet.time_signatures) return;
    
    annotationSheet.time_signatures.sort((a, b) => {
      // Sort by bar
      if (a.start_time.bar !== b.start_time.bar) {
        return a.start_time.bar - b.start_time.bar;
      }
      
      // If bars are equal, sort by beat
      if (a.start_time.beat !== b.start_time.beat) {
        return a.start_time.beat - b.start_time.beat;
      }
      
      // If beats are equal, sort by subdivision
      return a.start_time.subdivision - b.start_time.subdivision;
    });
  }

  /**
   * Sort keys by position
   */
  sortKeys() {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet || !annotationSheet.keys) return;

    annotationSheet.keys.sort((a, b) => {
      // Sort by bar
      if (a.start_time.bar !== b.start_time.bar) {
        return a.start_time.bar - b.start_time.bar;
      }
      
      // If bars are equal, sort by beat
      if (a.start_time.beat !== b.start_time.beat) {
        return a.start_time.beat - b.start_time.beat;
      }
      
      // If beats are equal, sort by subdivision
      return a.start_time.subdivision - b.start_time.subdivision;
    });
  }

  /**
   * Sort tempos by position
   */
  sortTempos() {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet || !annotationSheet.tempos) return;

    annotationSheet.tempos.sort((a, b) => {
      // Sort by bar
      if (a.start_time.bar !== b.start_time.bar) {
        return a.start_time.bar - b.start_time.bar;
      }
      
      // If bars are equal, sort by beat
      if (a.start_time.beat !== b.start_time.beat) {
        return a.start_time.beat - b.start_time.beat;
      }
      
      // If beats are equal, sort by subdivision
      return a.start_time.subdivision - b.start_time.subdivision;
    });
  }
}
